import React, { useEffect } from 'react';
import Product from './product/Product';
import Selectable from '../common/Selectable';
import { useTranslation } from 'react-i18next';
import Badge from 'components/common/badge/Badge';
import {
    ActiveClaimStatuses,
    FeatureState,
    ProductStatuses,
    RoutePaths
} from 'Constants/Common';
import { useAppSelector } from 'state/hooks';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
    setSelectedPolicy,
    setSelectedVehicle
} from 'state/slices/vehicleSlice';
import { PolicyState } from 'state/types/vehicle';
import { buildMissedPaymentPagePath } from 'helpers/routing';

export type PoliciesProps = {
    vehicleIndex: number;
    policies: PolicyState[];
};

const BadgePanel = ({
    policy,
    vehicleIndex
}: {
    policy: PolicyState;
    vehicleIndex: number;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation();
    const features = useAppSelector((s) => s.site.features);

    const showRepairRequestForPolicy = () => {
        dispatch(setSelectedVehicle(vehicleIndex));
        dispatch(setSelectedPolicy(policy.reference));
        navigate(RoutePaths.ViewRequests);
    };
    const openClaims =
        policy.status === ProductStatuses.Active &&
        policy.claims.filter((r) => ActiveClaimStatuses.includes(r.status));

    const renderMissedPayment = !!policy.missedPayment;
    const renderActiveClaim =
        features.ActiveClaim === FeatureState.On && !!openClaims.length;

    if (!renderMissedPayment && !renderActiveClaim) {
        return;
    }

    return (
        <div className='mt-3 d-flex flex-wrap gap-2'>
            {renderMissedPayment && (
                <Link to={buildMissedPaymentPagePath(policy.reference)}>
                    <Badge
                        data-testid='update-payment-badge-button'
                        color='warning'>
                        {t('update_payment')}
                    </Badge>
                </Link>
            )}
            {renderActiveClaim && (
                <Badge
                    className='position-relative'
                    color='info'
                    onClick={showRepairRequestForPolicy}
                    type='button'
                    data-testid='active-claim-badge-button'>
                    {t('active_claim')}
                    <Badge
                        className='position-absolute start-100 translate-middle'
                        color='secondary'>
                        {openClaims.length}
                    </Badge>
                </Badge>
            )}
        </div>
    );
};

const Policies = ({ vehicleIndex, policies }: PoliciesProps): JSX.Element => {
    const dispatch = useDispatch();
    const selectedPolicyReference = useAppSelector(
        (s) => s.vehicles.selectedPolicy?.reference
    );

    useEffect(() => {
        dispatch(setSelectedPolicy(''));
    }, []);

    const onPolicyClicked = (selected: boolean, policyReference: string) => {
        if (selected) {
            dispatch(setSelectedPolicy(policyReference));
            dispatch(setSelectedVehicle(vehicleIndex));
        } else {
            dispatch(setSelectedPolicy(''));
        }
    };

    return (
        <div className='card-body'>
            {policies.map((policy, index) => {
                return (
                    <Selectable
                        id={`selectable-product-${index.toString()}`}
                        onClicked={(selected) =>
                            onPolicyClicked(selected, policy.reference)
                        }
                        selected={policy.reference === selectedPolicyReference}
                        hasSelectableButton={true}
                        style={{
                            border: '1px solid #ced4da',
                            marginTop: '10px'
                        }}
                        key={`product-selectable-${vehicleIndex.toString()}-${index.toString()}`}>
                        <Product
                            id={`product-${vehicleIndex.toString()}-${index.toString()}`}
                            product={policy}
                        />
                        <BadgePanel
                            policy={policy}
                            vehicleIndex={vehicleIndex}
                        />
                    </Selectable>
                );
            })}
        </div>
    );
};

export default Policies;
